import type { FlightResponse } from 'generatedTypes/flight/models';
import type { TelemetryPointResponse } from 'generatedTypes/flight/models/telemetryPointResponse';
import type {
    Feature,
    FeatureCollection,
    LineString,
    MultiPolygon,
    Polygon,
    Position
} from 'geojson';
import { featureUtil } from 'utils/geoJSONFeatureUtils/featureUtil';

import { buildRateSegments, computePassRateGalPerAc } from './telemetrySegmentUtil';

type CoverageFlight = Pick<FlightResponse, 'id' | 'swathWidthMeters' | 'sprayPath'>;

type BuildCoverageFeaturesArgs = {
    flights: CoverageFlight[];
    telemetryPointsByFlightId: Record<string, TelemetryPointResponse[]>;
};

export type CoverageFeatureProperties = {
    flightId: string;
    rateGalPerAc: number;
};

const { createLinePolygonWithWidthMeters } = featureUtil;

const flattenToPolygonFeatures = (
    passPolygon: Feature<Polygon | MultiPolygon>,
    properties: CoverageFeatureProperties
): Array<Feature<Polygon, CoverageFeatureProperties>> => {
    if (passPolygon.geometry.type === 'MultiPolygon') {
        return passPolygon.geometry.coordinates.map(ringSet => ({
            geometry: { coordinates: ringSet, type: 'Polygon' },
            properties,
            type: 'Feature'
        }));
    }

    return [
        {
            geometry: passPolygon.geometry,
            properties,
            type: 'Feature'
        }
    ];
};

const buildFlightFeatures = ({
    flight,
    points
}: {
    flight: CoverageFlight;
    points: TelemetryPointResponse[];
}): Array<Feature<Polygon, CoverageFeatureProperties>> => {
    const { id, sprayPath, swathWidthMeters } = flight;
    const passes = sprayPath.coordinates as Position[][];

    if (points.length < 2 || passes.length === 0) {
        return [];
    }

    const segments = buildRateSegments({
        fallbackSwathMeters: swathWidthMeters,
        points
    });

    if (segments.length === 0) {
        return [];
    }

    const features: Array<Feature<Polygon, CoverageFeatureProperties>> = [];

    for (const passCoordinates of passes) {
        if (passCoordinates.length < 2) {
            continue;
        }

        const lineFeature: Feature<LineString> = {
            geometry: { coordinates: passCoordinates, type: 'LineString' },
            properties: null,
            type: 'Feature'
        };

        const passPolygon = createLinePolygonWithWidthMeters(
            lineFeature,
            swathWidthMeters / 2
        ) as unknown as Feature<Polygon | MultiPolygon>;

        const rateGalPerAc = computePassRateGalPerAc({ passPolygon, segments });

        if (rateGalPerAc === null) {
            continue;
        }

        features.push(
            ...flattenToPolygonFeatures(passPolygon, {
                flightId: id,
                rateGalPerAc
            })
        );
    }

    return features;
};

export function buildCoverageFeatures(
    args: BuildCoverageFeaturesArgs
): FeatureCollection<Polygon, CoverageFeatureProperties> {
    const { flights, telemetryPointsByFlightId } = args;

    const features: Array<Feature<Polygon, CoverageFeatureProperties>> = [];

    for (const flight of flights) {
        const points = telemetryPointsByFlightId[flight.id] ?? [];

        features.push(...buildFlightFeatures({ flight, points }));
    }

    return {
        features,
        type: 'FeatureCollection'
    };
}
